import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PRIMITIVE_MESH_PARAMS, type PrimitiveType } from '../../export/BuiltInPrimitives';
import type { SpatialInteractionDescriptor, SpatialObjectDescriptor } from './spatial-types';

export function primitiveCode(type: PrimitiveType): string {
    const { builder, options, rotateX } = PRIMITIVE_MESH_PARAMS[type];
    const call = `MeshBuilder.${builder}('p',{${Object.entries(options).map(([k, v]) => `${k}:${v}`).join(',')}})`;
    if (rotateX === undefined) return call;
    // Written as Math.PI/n, which is the editor's angle only when n is a whole number.
    const divisor = 180 / rotateX;
    if (!Number.isInteger(divisor)) throw new Error(`primitive ${type}: a ${rotateX}° turn is not Math.PI/n`);
    return `(()=>{const m=${call};m.rotation.x=Math.PI/${divisor};m.bakeCurrentTransformIntoVertices();return m;})()`;
}

const isPrimitive = (template: string): template is PrimitiveType => Object.hasOwn(PRIMITIVE_MESH_PARAMS, template);

/** `color`: the part's own colour, else the nearest group's in the bake that has one. */
interface BakePart { desc: SpatialObjectDescriptor; type: PrimitiveType; matrix: Matrix; color?: string }

export interface BakePlan {
    code: Map<string, string>;
    folded: Set<string>;
}

function localMatrix(d: SpatialObjectDescriptor): Matrix {
    const r = d.rotation ?? [0, 0, 0], deg = Math.PI / 180;
    return Matrix.Compose(new Vector3(...(d.scale ?? [1, 1, 1])), Quaternion.FromEulerAngles(r[0] * deg, r[1] * deg, r[2] * deg), new Vector3(...(d.position ?? [0, 0, 0])));
}

const num = (n: number) => String(Math.round(n * 1e6) / 1e6 + 0);

export function planBakes(descriptors: SpatialObjectDescriptor[], interactions: SpatialInteractionDescriptor[] = []): BakePlan {
    const plan: BakePlan = { code: new Map(), folded: new Set() };
    const roots = descriptors.filter(d => d.bake);
    if (!roots.length) return plan;
    const children = new Map<string, SpatialObjectDescriptor[]>();
    for (const d of descriptors) {
        if (!d.parentId) continue;
        const siblings = children.get(d.parentId);
        if (siblings) siblings.push(d);
        else children.set(d.parentId, [d]);
    }
    const wired = new Set(interactions.flatMap(i => [i.sourceId, i.targetId, i.scopeId ?? '', ...(i.exclusiveGroupIds ?? [])]));
    for (const root of roots) {
        const parts: BakePart[] = [];
        const visit = (d: SpatialObjectDescriptor, matrix: Matrix, inherited?: string) => {
            const what = d === root ? `bake("${root.name}")` : `bake("${root.name}"): "${d.name}"`;
            if (d.template !== 'empty' && !isPrimitive(d.template)) {
                throw new Error(`${what} is a ${d.template}; only cube, sphere, cylinder, plane and plain groups can be baked. Leave it outside the baked group.`);
            }
            const bound = Object.entries(bakeSpec(d)).filter(([, value]) => typeof value === 'string' && value.startsWith('{')).map(([key]) => key);
            const carried = [
                ...(d.behaviors ?? []).map(b => `a ${b.type} behavior`),
                ...(d.imagePrompt ? ['an image'] : []), ...(d.inspector ? ['an inspector'] : []),
                ...(wired.has(d.id) ? ['an interaction'] : []), ...(d.active === false ? ['setActive(false)'] : []),
                ...bound.map(key => `a {property} ${key}`),
            ];
            if (carried.length) {
                throw new Error(`${what} carries ${carried.join(', ')}; a baked part is geometry only. `
                    + 'Put behaviors on a group around the baked group, or leave that object outside it.');
            }
            const color = bakeSpec(d).color ?? inherited;
            if (isPrimitive(d.template)) parts.push({ desc: d, type: d.template, matrix, color });
            for (const child of children.get(d.id) ?? []) {
                if (child.bake) throw new Error(`bake("${child.name}") is inside baked group "${root.name}"; bake the outer group only.`);
                plan.folded.add(child.id);
                visit(child, localMatrix(child).multiply(matrix), color);
            }
        };
        visit(root, Matrix.Identity());
        if (!parts.length) throw new Error(`bake("${root.name}") has no primitives to bake.`);
        plan.code.set(root.id, bakeCode(parts));
    }
    return plan;
}

/** The material a baked part keeps: its material() fields, with color() as the base colour. */
function bakeSpec(desc: SpatialObjectDescriptor) {
    return { ...desc.material, color: desc.material?.color ?? desc.color };
}

function bakeCode(parts: BakePart[]): string {
    const slots = new Map<string, number>();
    const lines = parts.map(({ desc, type, matrix, color }) => {
        const spec = bakeSpec(desc);
        const key = JSON.stringify({ color: color ?? null, metallic: spec.metallic ?? 0, roughness: spec.roughness ?? 0.5, emissive: spec.emissive ?? null, opacity: spec.opacity ?? null });
        if (!slots.has(key)) slots.set(key, slots.size);
        return `add(${primitiveCode(type)},[${Array.from(matrix.asArray()).map(num).join(',')}],${slots.get(key)});`;
    });
    // The editor's primitive material is metallic 0, roughness 0.5.
    const materials = [...slots.keys()].map((key, i) => {
        const s = JSON.parse(key) as Record<string, unknown>;
        const bag = Object.fromEntries(Object.entries(s).filter(([, v]) => v !== null));
        return `material('${typeof s.color === 'string' ? s.color : 'Default'} ${i}',${JSON.stringify(bag)})`;
    });
    return [
        `const M=[${materials.join(',')}],P=[];`,
        `const add=(m,a,i)=>{m.bakeTransformIntoVertices(Matrix.FromArray(a));m.material=M[i];P.push(m);};`,
        ...lines,
        `return merge(P,'Part');`,
    ].join('\n');
}
